import { ethers } from "ethers";
import axios from "axios";
import pinataSDK from "@pinata/sdk";
import fs from "fs";
import { getContracts } from "../utils/getContracts";
import { formatUnits, parseEther } from "ethers";

// Initialize Pinata SDK
const pinata = new pinataSDK({
  pinataJWTKey:
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySW5mb3JtYXRpb24iOnsiaWQiOiI2NDgyZjkxZS0zNjNlLTQ5OWYtYjQ1ZC0wNzNmYzA4NmQxYTUiLCJlbWFpbCI6InRlaGlsYWZhdm91cml0ZUBnbWFpbC5jb20iLCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwicGluX3BvbGljeSI6eyJyZWdpb25zIjpbeyJpZCI6IkZSQTEiLCJkZXNpcmVkUmVwbGljYXRpb25Db3VudCI6MX1dLCJ2ZXJzaW9uIjoxfSwibWZhX2VuYWJsZWQiOmZhbHNlLCJzdGF0dXMiOiJBQ1RJVkUifSwiYXV0aGVudGljYXRpb25UeXBlIjoic2NvcGVkS2V5Iiwic2NvcGVkS2V5S2V5IjoiNTczMGRiYjgwNjhiZjFjYjdkYTUiLCJzY29wZWRLZXlTZWNyZXQiOiI1MTkyMmJmNDM1ZGUwYWM5YWMwN2UzYmE4NzQ1OGZiYzU5N2FjOTI3MzJhOTJjMGFlNjQyYmQ1OWNhZWRkMzk4IiwiaWF0IjoxNzAwMjA4NzQzfQ.t850Nq1NQGZTnpw-f-NEhVUrk892-UBFUWOp6hkiqnk",
});

const abiCoder = new ethers.AbiCoder();

// Encoding functions
function encodeStaticAttributes(
  facilityName: string,
  facilityDescription: string,
  facilityLocation: string,
  facilityCategory: string,
  userID: string,
  subscriptionPlan: string,
  verifier: string
): string {
  const encodedStaticAttributes = abiCoder.encode(
    ["string", "string", "string", "string", "string", "string", "string"],
    [
      facilityName,
      facilityDescription,
      facilityLocation,
      facilityCategory,
      userID,
      subscriptionPlan,
      verifier,
    ]
  );
  console.log("Encoded Static Attributes:", encodedStaticAttributes);
  return encodedStaticAttributes;
}

function encodeDynamicAttributes(
  reportingPeriod: string,
  directStationaryEmissionsValue: number,
  totalGHGEmissions: number,
  quantifiedGHGEmissionsInBaselineScenario: number,
  facilityCapacity: number
): string {
  const convertedDirectStationaryEmissionsValue = ethers.parseEther(
    directStationaryEmissionsValue.toString()
  );
  const convertedTotalGHGEmissions = ethers.parseEther(
    totalGHGEmissions.toString()
  );
  const convertedQuantifiedGHGEmissionsInBaselineScenario =
    ethers.parseEther(
      quantifiedGHGEmissionsInBaselineScenario.toString()
    );
  const convertedFacilityCapacity = ethers.parseEther(
    facilityCapacity.toString()
  );

  const encodedDynamicAttributes = abiCoder.encode(
    ["string", "uint256", "uint256", "uint256", "uint256"],
    [
      reportingPeriod,
      convertedDirectStationaryEmissionsValue,
      convertedTotalGHGEmissions,
      convertedQuantifiedGHGEmissionsInBaselineScenario,
      convertedFacilityCapacity,
    ]
  );
  console.log("Encoded Dynamic Attributes:", encodedDynamicAttributes);
  return encodedDynamicAttributes;
}
async function balanceOf(address: string) {
  const { closeCarbonTokenContractInstance } = await getContracts();
  const balance = await closeCarbonTokenContractInstance.balanceOf(address);
  return balance;
}
// Role Management Functions
async function grantRole(role: number, account: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    let roleBytes32: string;
    switch (role) {
      case 1:
        roleBytes32 = ethers.keccak256(
          ethers.toUtf8Bytes("VALIDATOR_ROLE")
        );
        break;
      case 2:
        roleBytes32 = ethers.keccak256(
          ethers.toUtf8Bytes("VERIFIER_ROLE")
        );
        break;
      case 3:
        roleBytes32 = ethers.keccak256(
          ethers.toUtf8Bytes("ADMIN_ROLE")
        );
        break;
      default:
        throw new Error("Invalid role number");
    }

    const tx = await businessInfrastructureContractInstance.grantRole(
      roleBytes32,
      account,
      true
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      message: `Role granted to ${account}`,
    };
  } catch (error: any) {
    console.error("Error granting role:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

// Fee Management Functions
async function setFees(
  validatorFees: number,
  verifierFees: number,
  ownerFees: number
) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const tx = await businessInfrastructureContractInstance.setFees(
      validatorFees,
      verifierFees,
      ownerFees
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      validatorFees,
      verifierFees,
      ownerFees,
    };
  } catch (error: any) {
    console.error("Error setting fees:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

// Contract Address Management
async function setClosedCarbonTokenAddress(address: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const tx =
      await businessInfrastructureContractInstance.setClosedCarbonTokenAddress(
        address
      );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      address,
    };
  } catch (error: any) {
    console.error("Error setting carbon token address:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

async function setInfrastructureChangeNFTAddress(address: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const tx =
      await businessInfrastructureContractInstance.setInfrastructureChangeNFTAddress(
        address
      );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      address,
    };
  } catch (error: any) {
    console.error("Error setting Infrastructure Change NFT address:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

// KYC and Plant Management Functions
async function performKYC(plantAddress: string, staticAttributes: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const tx = await businessInfrastructureContractInstance.performKYC(
      plantAddress,
      staticAttributes
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      plantAddress,
    };
  } catch (error: any) {
    console.error("Error performing KYC:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

async function validatePlant(plantAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const tx = await businessInfrastructureContractInstance.validate(
      plantAddress
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      plantAddress,
    };
  } catch (error: any) {
    console.error("Error validating plant:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

async function recordPlant(
  plantAddresses: string[],
  dynamicAttributes: string[]
) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const tx = await businessInfrastructureContractInstance.recordPlant(
      plantAddresses,
      dynamicAttributes
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      plantsRecorded: plantAddresses.length,
    };
  } catch (error: any) {
    console.error("Error recording plants:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

async function verifyAndMint(plantAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const tx = await businessInfrastructureContractInstance.verifyAndMint(
      plantAddress
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      plantAddress,
    };
  } catch (error: any) {
    console.error("Error verifying and minting:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

async function issueCarbonCredits(plantAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const tx = await businessInfrastructureContractInstance.issueCarbonCredits(
      plantAddress
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      plantAddress,
    };
  } catch (error: any) {
    console.error("Error issuing carbon credits:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

// Read Functions
async function getStaticAttributes(plantAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const staticAttributes =
      await businessInfrastructureContractInstance.getStaticAttributes(
        plantAddress
      );

    return {
      facilityName: staticAttributes[0],
      facilityDescription: staticAttributes[1],
      facilityLocation: staticAttributes[2],
      facilityCategory: staticAttributes[3],
      userID: staticAttributes[4],
      subscriptionPlan: staticAttributes[5],
      verifier: staticAttributes[6],
    };
  } catch (error: any) {
    console.error("Error getting static attributes:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getDynamicAttributes(plantAddress: string, index: number) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const dynamicAttributes =
      await businessInfrastructureContractInstance.dynamicAttributes(
        plantAddress,
        index
      );

    return {
      reportingPeriod: dynamicAttributes.reportingPeriod,
      directStationaryEmissionsValue: ethers.formatEther(
        dynamicAttributes.directStationaryEmissionsValue
      ),
      totalGHGEmissions: ethers.formatEther(
        dynamicAttributes.totalGHGEmissions
      ),
      quantifiedGHGEmissionsInBaselineScenario: ethers.formatEther(
        dynamicAttributes.quantifiedGHGEmissionsInBaselineScenario
      ),
      facilityCapacity: ethers.formatEther(
        dynamicAttributes.facilityCapacity
      ),
    };
  } catch (error: any) {
    console.error("Error getting dynamic attributes:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllEmissionValues(plantAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const allEmissionValues =
      await businessInfrastructureContractInstance.allEmissionValues(
        plantAddress
      );

    return {
      reportingPeriod: allEmissionValues.reportingPeriod,
      directStationaryEmissionsValue: ethers.formatEther(
        allEmissionValues.directStationaryEmissionsValue
      ),
      totalGHGEmissions: ethers.formatEther(
        allEmissionValues.totalGHGEmissions
      ),
      quantifiedGHGEmissionsInBaselineScenario: ethers.formatEther(
        allEmissionValues.quantifiedGHGEmissionsInBaselineScenario
      ),
      facilityCapacity: ethers.formatEther(
        allEmissionValues.facilityCapacity
      ),
      isVerified: allEmissionValues.isVerified,
    };
  } catch (error: any) {
    console.error("Error getting all emission values:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getPlantInfo(plantAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const plantInfo = await businessInfrastructureContractInstance.plantInfo(
      plantAddress
    );

    return {
      plantAddress: plantInfo.plantAddress,
      organizationName: plantInfo.organizationName,
      scenario: plantInfo.scenario,
    };
  } catch (error: any) {
    console.error("Error getting plant info:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getPlantEmissionData(plantAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const emissionData =
      await businessInfrastructureContractInstance.getPlantEmissionData(
        plantAddress
      );

    const formattedData = emissionData.map((emission: any) => ({
      reportingPeriod: emission.reportingPeriod,
      directStationaryEmissionsValue: ethers.formatEther(
        emission.directStationaryEmissionsValue
      ),
      totalGHGEmissions: ethers.formatEther(emission.totalGHGEmissions),
      quantifiedGHGEmissionsInBaselineScenario: ethers.formatEther(
        emission.quantifiedGHGEmissionsInBaselineScenario
      ),
      facilityCapacity: ethers.formatEther(emission.facilityCapacity),
      isVerified: emission.isVerified,
    }));

    return {
      plantAddress,
      emissionData: formattedData,
    };
  } catch (error: any) {
    console.error("Error getting plant emission data:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllPlantsLength() {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const length =
      await businessInfrastructureContractInstance.getAllPlantsLength();

    return {
      totalPlants: length.toString(),
    };
  } catch (error: any) {
    console.error("Error getting all plants length:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllPlantEmissionLength(plantAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const length =
      await businessInfrastructureContractInstance.getAllPlantEmissionLength(
        plantAddress
      );

    return {
      emissionRecords: length.toString(),
    };
  } catch (error: any) {
    console.error("Error getting plant emission length:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllUserEmissionLength(plantAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const length =
      await businessInfrastructureContractInstance.allUserEmissionLength(
        plantAddress
      );

    return {
      userEmissionRecords: length.toString(),
    };
  } catch (error: any) {
    console.error("Error getting user emission length:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllPlantsLengthDirectCall() {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const length =
      await businessInfrastructureContractInstance.allPlantsLength();

    return {
      totalPlants: length.toString(),
    };
  } catch (error: any) {
    console.error("Error getting all plants length (direct call):", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getPlantsData(start: number, count: number) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const plantsData =
      await businessInfrastructureContractInstance.getPlantsData(start, count);

    const formattedData = plantsData.map((plant: any) => ({
      plantAddress: plant.plantAddress,
      organizationName: plant.organizationName,
      scenario: plant.scenario,
      emissionData: {
        reportingPeriod: plant.emissionData.reportingPeriod,
        directStationaryEmissionsValue: ethers.formatEther(
          plant.emissionData.directStationaryEmissionsValue
        ),
        totalGHGEmissions: ethers.formatEther(
          plant.emissionData.totalGHGEmissions
        ),
        quantifiedGHGEmissionsInBaselineScenario: ethers.formatEther(
          plant.emissionData.quantifiedGHGEmissionsInBaselineScenario
        ),
        facilityCapacity: ethers.formatEther(
          plant.emissionData.facilityCapacity
        ),
        isVerified: plant.emissionData.isVerified,
      },
      isKYCPerformed: plant.isKYCPerformed,
      isPlantRecorded: plant.isPlantRecorded,
      isCarbonCreditIssued: plant.isCarbonCreditIssued,
      isValidated: plant.isValidated,
      isVerified: plant.isVerified,
    }));

    return {
      plantsData: formattedData,
    };
  } catch (error: any) {
    console.error("Error getting plants data:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function isKYCPerformed(userAddress: string) {
  try {
    const { businessInfrastructureContractInstance } = await getContracts();

    const kycStatus =
      await businessInfrastructureContractInstance.isKYCPerformed(userAddress);

    return {
      userAddress,
      isKYCPerformed: kycStatus,
    };
  } catch (error) {
    console.error("Error checking KYC status:", error);
    return {
      error: (error as Error).message,
    };
  }
}

// Export all functions
export {
  balanceOf,
  encodeStaticAttributes,
  encodeDynamicAttributes,
  grantRole,
  setFees,
  setClosedCarbonTokenAddress,
  setInfrastructureChangeNFTAddress,
  performKYC,
  validatePlant,
  recordPlant,
  verifyAndMint,
  issueCarbonCredits,
  getStaticAttributes,
  getDynamicAttributes,
  getAllEmissionValues,
  getPlantInfo,
  getPlantEmissionData,
  getAllPlantsLength,
  getAllPlantEmissionLength,
  getAllUserEmissionLength,
  getAllPlantsLengthDirectCall,
  getPlantsData,
  isKYCPerformed,
};

// Example usage (commented out)
/*
// Grant admin role
grantRole(3, "0xE05b38e08EA9aC632639deCb31258bDef3533E04")
  .then((result) => {
    console.log("Grant role result:", result);
  })
  .catch((err) => {
    console.log("Grant role error:", err);
  });

// Example static attributes encoding
const facilityName = "Industrial Plant Alpha";
const facilityDescription = "Large-scale industrial facility with infrastructure improvements";
const facilityLocation = "Lagos, Nigeria";
const facilityCategory = "Industrial Infrastructure";
const userID = "user123";
const subscriptionPlan = "Premium";
const verifier = "Verifier1";

const encodedStatic = encodeStaticAttributes(
  facilityName,
  facilityDescription,
  facilityLocation,
  facilityCategory,
  userID,
  subscriptionPlan,
  verifier
);

// Example dynamic attributes encoding
const reportingPeriod = "Q1 2024";
const directStationaryEmissionsValue = 800;
const totalGHGEmissions = 1200;
const quantifiedGHGEmissionsInBaselineScenario = 2000;
const facilityCapacity = 3000;

const encodedDynamic = encodeDynamicAttributes(
  reportingPeriod,
  directStationaryEmissionsValue,
  totalGHGEmissions,
  quantifiedGHGEmissionsInBaselineScenario,
  facilityCapacity
);
*/

// Test function call
getAllPlantsLength()
  .then((result) => {
    console.log("Total plants:", result);
  })
  .catch((err) => {
    console.log("Error getting plants length:", err);
  });
